export interface FolderedAwardResponse {
    status: string;
    message: string;
    execution_time_utc: Date;
    warnings: any[];
    status_code: number;
    data: AwardData;
}

export interface AwardData {
    folderedAwards: FolderedAward[];
    groupImageUrl: string;
}

export interface FolderedAward {
    Key: string;
    Value: ContextAwardItem[];
}

export interface ContextAwardItem {
    rule: Rule;
    badgeImageID: number; // Award Image ID
    coinAmount: number; // Award Amount
}

export interface Rule {
    RuleID: number;
    CompanyID: number;
    RuleName: string; // Award Name
    RuleDescription: string;
    RuleLevel: number;
    StartDate: Date;
    StartDate_ForView: string;
    EndDate: Date | null;
    EndDate_ForView: string;
    IntervalType: number;
    IntervalAmount: number;
    SubmittedBy: number;
    RuleFolder: string;
    Timestamp: string;
    GameType?: GameType;
    GameCategoryType?: GameCategoryType;
    FolderID: number;
    Active: boolean;
    DateAdded: Date;
    DateAdded_UTC: Date;
    DateRemoved?: Date;
    AutoJoin: boolean;
    IntervalType_ForView: IntervalTypeForView;
    HasCoin: boolean;
    HasXP: boolean;
    HasBadge: boolean;
    HasProficiency: boolean;
    HasCoaching: boolean;
    HasSurvey: boolean;
    HasChallengeTemplate: boolean;
    HasCurrency: boolean;
    HasConnect3Payout: boolean;
    IsCopy: boolean;
    RuleType: number;
    RuleGroups: RuleGroup[];
    RuleAttributes: RuleAttribute[];
    RuleCoinPayout: RulePayout;
    RuleXPPayout: RulePayout;
    RuleCurrencyPayout: RulePayout;
    RulePayoutCollection: RulePayout[];
    RuleLevelCollections: Array<EveryoneElseLevel[]>;
    RemovedRuleLevels: RuleLevel[];
    RuleBadgeInfoCollection: BadgeInfo[];
    RuleCoachingInfoCollection: RuleCoachingInfoDTO[];
    RuleProficiencyInfoCollection: RuleProficiencyInfo[];
    RuleChallengeTemplate: RuleChallengeTemplate;
    RuleGroupCollection: RuleGroup[];
    RuleRolesCollection: SelectedPartialRole[];
    PreloadedPartialRoles: number[];
    EveryoneElseLevel: EveryoneElseLevel;
}

export interface RuleAttribute {
    CompanyID: number;
    RuleID: number;
    CategoryID: number;
    AttributeID: number;
    CategorySet: number;
    CategoryMask: bigint;
    AttributeValue: bigint;
}

export enum GameType {
    MultiplierMadness = 1,
    TopDog = 2,
    Bullseye = 3,
    Percentile = 4,
    Milestone = 5,
    Connect3 = 6,
    Blackout = 7,
    TopTeam = 8,
    Raffle = 9,
    BracketTournament = 10
}

export enum GameCategoryType {
    Singleplayer = 1,
    Multiplayer = 2,
    TeamVs = 3,
}

export interface RulePayout {
    RulePayoutID: number;
    RuleID: number;
    CompanyID: number;
    Multiplier: number;
    MaxPayout?: number;
    PayoutType: number;
    Active: boolean;
    DateAdded?: Date;
    DateRemoved?: Date;
    Timestamp: string;
    DelayedAmount?: number;
    DelayedType?: PayoutDelayType;
}

export enum PayoutDelayType {
    Now = 0,
    Days = 1,
    Weeks = 2,
    Months = 3,
}

export class Schedule_WSTO {
    SchedType: string;
    MonthlyType: string;
    StartDate: Date;
    StartHour: number;
    StartMin: number;
    StartTOD: number;
    EndDate?: Date;
    EndHour?: number;
    EndMin?: number;
    EndTOD: number;
    RecurringAmount: number;
    RepeatIncrement: number;
    DOM: OfMonth_WSTO[];
    DOMOut: OfMonth_WSTO[];
    WOM: OfMonth_WSTO[];
    WOMOut: OfMonth_WSTO[];
    DOW: boolean[];
}

export interface OfMonth_WSTO {
    text: string;
    value: number;
    isSelected: boolean;
}

export interface EveryoneElseLevel {
    RuleLevelID: number;
    RuleID: number;
    CompanyID: number;
    RangeMin: null;
    RangeMinCompare: number;
    RangeMax: null;
    RangeMaxCompare: number;
    LevelType: number;
    IsDeleted: boolean;
    DateAdded: Date | null;
    DateRemoved: null;
    Timestamp: null | string;
    LevelType_ForView: LevelTypeForView;
    BaseLevelActionCollection: BaseLevelActionCollection[];
}

export interface RuleLevel {
    RuleLevelID: number;
    RuleID: number;
    CompanyID: number;
    RangeMin?: number;
    RangeMax?: number;
    LevelType: RuleLevelTypes;
    IsDeleted: boolean;
    DateAdded?: Date;
    DateRemoved?: Date;
    Timestamp: string;
}

export enum RuleLevelTypes {
    Rank = 0,
    Range = 1,
    Percentile = 2,
    Manual = 3,
    Connect3 = 4,
    Ticket = 5,
}

export interface BadgeInfo {
    BadgeInfoID: number;
    RuleID: number;
    CompanyID: number;
    BadgeName: string;
    Description: string;
    RuleLevelType: RuleLevelTypes;
    MaxTimesEarned?: number;
    IntervalsEarned?: number;
    BadgeFolder: string;
    Timestamp: string;
}

export interface RuleCoachingInfoDTO {
    CoachingInfoID: number;
    RuleID: number;
    CompanyID: number;
    RuleLevelType: RuleLevelTypes;
    DateRange: string;
    RoleToSendID: number;
    RoleToSendLevel: number;
    Timestamp: string;
}

export interface RuleProficiencyInfo {
    ProficiencyInfoID: number;
    RuleID: number;
    CompanyID: number;
    RuleLevelType: RuleLevelTypes;
    ScheduleID: number;
    Schedule: Schedule_WSTO;
    MinAgentsPerSkill?: number;
    AutoChangeSkill: boolean;
    Timestamp: string;
}

export class RuleChallengeTemplate {
    CompanyID: number;
    ChallengeTemplateID: number;
    RuleID: number;
    ChallengeTemplateName: string;
    ChallengeTemplateDesc: string;
    PayoutLevel: number;
    SeedPoints: number;
    IsPer: boolean;
    EntryMin: number;
    EntryMax?: number;
    ParticipantsMin: number;
    ParticipantsMax?: number = null;
    LevelPlusMinus?: number = null;
    LevelMin: number;
    LevelMax?: number = null;
    WithdrawDays?: number = null;
    OpenCreate: boolean;
    TieredPayout: string;
    Active: boolean;
    ActiveSDC: boolean;
}

export interface BaseLevelActionCollection {
    ActionType: number;
    CoinAction: Action | null;
    XPAction: Action | null;
    CustomAction: null;
    CurrencyAction: null;
    BadgeAction: BadgeAction | null;
    CoachingAction: null;
    SurveyAction: null;
    ProficiencyAction: null;
}

export interface BadgeAction {
    LevelBadgeID: number;
    RuleLevelID: number;
    RuleID: number;
    CompanyID: number;
    BadgeLevelName: string;
    ImageID: number;
    ImagePath: string;
    IsDeleted: boolean;
    DateAdded: Date;
    DateRemoved: null;
    Timestamp: string;
    GivenByName: null;
    RuleType: number;
    GameType: number;
}

export interface Action {
    RuleLevelPayoutID: number;
    RuleLevelID: number;
    RuleID: number;
    CompanyID: number;
    PayoutType: number;
    Value: number;
    IsDeleted: boolean;
    DateAdded: Date;
    DateRemoved: null;
    Timestamp: string;
    DelayedAmount: null;
    DelayedType: null;
}

export enum LevelTypeForView {
    Manual = "Manual",
    Rank = "Rank",
}

export enum IntervalTypeForView {
    Once = "Once",
}

export interface RuleGroup {
    RuleGroupID: number;
    CompanyID: number;
    RuleID: number;
    GroupID: number;
    GroupLevel: number;
    GroupType: number;
    Timestamp: string;
}

export interface RuleRolesCollection {
    RuleID: number;
    CompanyID: number;
    ProfileID: number;
    RoleName: string;
}

export interface SelectedPartialRole {
    RoleName: string;
    RoleID: number;
    RoleLevels: null;
}

export interface AwardGroupData {
    groupID: number;
    groupLevel: number;
}

export interface AwardPermissionResponse {
    status: string;
    message: string;
    execution_time_utc: Date;
    warnings: any[];
    status_code: number;
    data: AwardPermission;
}

export interface AwardPermission {
    LoginID: number;
    HasCreateAccess: boolean;
}
